/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect } from "react";
import { Tab, Tabs } from "@mui/material";
import { styled } from "@mui/system";
import Head from "next/head";
import { ArtworkInfo } from "./info/ArtworkInfo";
import { ArtworkDetails } from "./artworkDetails/ArtworkDetails";
import { ContentWrapper } from "~/features/contentWrapper";
import { colors, fontFamilies } from "~/theme";
import { useDefaultPdpTab } from "~/features/remoteConfig/useDefaultPdpTab";
import { getPDPPageApplication } from "~/api/application/getPDPpageApplication";
import { TabPanel, TabHashes, TAB_VALUES } from "~/features/PDP/TabPanel";
import { TokenDataSchema } from "~/api/ERC721v4/TokenDataJSONSchema";

const ARTWORK_TAB_VALUES = {
    [TabHashes.ARTWORK_DETAILS]: 0,
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
    boxShadow: `inset 0px -1px 0px ${colors.black15}`,
    marginTop: 93,
    padding: 0,
    [theme.breakpoints.down(767)]: {
        marginTop: 70,
    },
    [theme.breakpoints.down(768)]: {
        left: "50%",
        right: "50%",
        width: "100vw",
        position: "relative",
        maxWidth: "100vw",
        marginLeft: "-50vw",
        marginRight: "-50vw",
    },
    "& button": {
        minWidth: "auto",
        textTransform: "capitalize",
        paddingLeft: "24px",
        paddingRight: "24px",
        marginRight: 0,
        fontFamily: fontFamilies.poppins,
        fontSize: "16px",
        fontWeight: 600,
        "&:last-child": {
            marginRight: 0,
        },
        [theme.breakpoints.down(768)]: {
            fontFamily: fontFamilies.poppins,
            fontSize: "14px",
            paddingLeft: "14px",
            paddingRight: "14px",
        },
    },
}));

function a11yProps(index: number) {
    return {
        id: `tab-${index}`,
        "aria-controls": `tabpanel-${index}`,
    };
}

export interface PDPv4Props {
    tokenData: TokenDataSchema;
    isPDPV4Enabled?: boolean;
}

export function PDPv4(props: PDPv4Props) {
    const { tokenData, isPDPV4Enabled } = props;
    const [tabValue, setTabValue] = React.useState<number>(
        isPDPV4Enabled ? 0 : TAB_VALUES[TabHashes.EDITIONS_ACTIVITY],
    );

    const onChangeDefaultPdpTab = useCallback((value) => {
        setTabValue(TAB_VALUES[value]);
    }, []);

    useDefaultPdpTab({
        application: getPDPPageApplication(),
        onValue: onChangeDefaultPdpTab,
    });

    const handleTabChange = useCallback(
        (event: React.ChangeEvent<{}>, newValue: number) => {
            setTabValue(newValue);
        },
        [],
    );

    useEffect(() => {
        if (window) {
            const listener = () => {
                setTabValue(TAB_VALUES[window.location.hash]);
            };
            window.addEventListener("hashchange", listener, false);

            return () => window.removeEventListener("hashchange", listener);
        }
        return undefined;
    }, []);

    return (
        <>
            <Head>
                <title>
                    {tokenData?.metadata?.properties.name.description}
                </title>
            </Head>
            <ContentWrapper>
                <ArtworkInfo tokenData={tokenData} hasActionButton={false} />
                <>
                    <StyledTabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="artwork tabs"
                        variant="scrollable"
                    >
                        <Tab label="Details" {...a11yProps(0)} />
                    </StyledTabs>
                    <a id={TabHashes.ARTWORK_DETAILS.substring(1)} />
                    <TabPanel
                        value={tabValue as number}
                        index={ARTWORK_TAB_VALUES[TabHashes.ARTWORK_DETAILS]}
                    >
                        <ArtworkDetails tokenData={tokenData} />
                    </TabPanel>
                </>
            </ContentWrapper>
        </>
    );
}
